import React from 'react';
import cx from 'classnames';

import withStyles from '@mui/styles/withStyles';

import { createStdRequest, apiFetch, validateAcceptablePassword, showAlert } from '../utils/utils';

import { extractInitialsFromFullName } from '../utils/activityProcessor';

import CrmTextField from './CrmTextField';
import CrmButton from './CrmButton';
import AlertModal from './AlertModal';

const userProfilePanelStyles = {
  titleBoxContainer: {},
  titleBox: {
    height: '58px',
    width: '395px',
    display: 'flex',
    alignItems: 'center',
  },
  titleIcon: {
    height: '50px',
    width: '50px',
    margin: '0 10px 0 15px',
    position: 'relative',
    display: 'flex',
  },
  titleBox_text: {
    display: 'flex',
    flexFlow: 'column nowrap',
    width: 'calc(100% - 75px)',
    '&:after': {
      content: '',
      clear: 'both',
    },
  },
  title: {
    color: '#0c0d0d',
    fontSize: '20px',
    fontWeight: 500,
    lineHeight: '28px',
    cursor: 'pointer',
    maxWidth: '70%',
    '&:hover': {
      color: '#0c0dee',
    },
  },
  subtitle: {
    color: '#0c0d0d',
    fontSize: '13px',
    fontWeight: 400,
    lineHeight: '19px',
  },
  emailDiv: {
    marginTop: '12px',
  },
  emailSpan: {
    fontWeight: 600,
  },
  profileImageComponent: {
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    textAlign: 'center',
    backgroundColor: '#dce3e6',
    color: 'rgba(12,13,13,0.32)',
    position: 'relative',
    fontSize: '20px',
    fontWeight: 500,
    lineHeight: '50px',
  },
  profileImageComponent_circle: {
    verticalAlign: 'center',
    borderRadius: '50%',
  },
  changePasswordLinkDiv: {
    marginTop: '16px',
    marginBottom: '10px',
  },
  changePasswordLinkSpan: {
    cursor: 'pointer',
    fontWeight: 400,
    '&:hover': {
      color: '#00e',
    },
  },
  changePasswordForm: {
    border: '1px solid black',
  },
  hidePasswordForm: {
    display: 'none',
  },
  textField: {
    margin: '0 10px 0',
    width: 240,
  },
  passwordButtonsDiv: {
    textAlign: 'right',
    padding: '12px 8px 8px 0',
  },
  uploadPhotoDiv: {
    marginTop: '16px',
  },
  u_ellipsis: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};

/*
Call this with
      <SuperAdminProfilePanel
        userInfo={userInfo}
      />
*/

class UserProfilePanel extends React.Component {
  state = {
    showPasswordForm: false,
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
    specialUsers: [],
  };

  componentDidMount() {
    const uri = '/api/superAdmin/specialUsers';
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (json) => {
      this.setState({ specialUsers: json });
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  showPasswordUi() {
    this.setState({ showPasswordForm: !this.state.showPasswordForm });
  }

  hidePasswordUi() {
    this.setState({ showPasswordForm: false });
  }

  handlePasswordChange(event, stateKey) {
    const newValue = event.target.value.trim();
    this.setState({ [stateKey]: newValue });
  }

  validateFormAndSubmitPasswordChange() {
    const validation = this.validateForm();
    if (validation.valid) {
      const uri = '/api/account/resetPassword';
      const apiStruct = {
        email: this.state.emailOfChosenUser,
        oldPassword: this.state.oldPassword,
        newPassword: this.state.newPassword,
      };
      const request = createStdRequest(uri, 'post', apiStruct);
      const actionOnGoodResponse = (json) => {
        if (json.success) {
          this.hidePasswordUi();
          this.clearForm();
        } else {
          showAlert(this, json.errorMessage);
        }
      };
      apiFetch(this, request, actionOnGoodResponse);
    } else {
      showAlert(this, validation.errorMessage);
    }
  }

  validateForm() {
    const validation = validateAcceptablePassword(this.state.newPassword);
    if (!validation.valid) {
      return validation;
    }

    if (this.state.confirmPassword !== this.state.newPassword) {
      return {
        valid: false,
        errorMessage: "Your New Password and your ConfirmPassword don't match",
      };
    }

    if (!this.state.emailOfChosenUser) {
      return {
        valid: false,
        errorMessage: 'You need to choose a user from the among the Select options',
      };
    }

    return { valid: true };
  }

  clearForm() {
    this.freshenState();
  }

  freshenState() {
    const freshState = {
      showPasswordForm: false,
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    };
    this.setState(freshState);
  }

  render() {
    const { classes, userInfo } = this.props;
    const fullName = userInfo.prefName;
    const initials = extractInitialsFromFullName(fullName);
    const userCategory = userInfo.role;
    const classForPasswordForm = this.state.showPasswordForm ? classes.changePasswordForm : classes.hidePasswordForm;
    return (
      <div>
        <AlertModal show={this.state.shouldShowAlertModal} renderer={this} text={this.state.alertModalText} />

        <div className={classes.titleBoxContainer}>
          <div className={classes.titleBox}>
            <div className={classes.titleIcon}>
              <div className={cx(classes.profileImageComponent, classes.profileImageComponent_circle)}>{initials}</div>
            </div>
            <div className={classes.titleBox_text}>
              <div className={cx(classes.title, classes.u_ellipsis)}>{fullName}</div>
              <div className={cx(classes.subtitle, classes.u_ellipsis)}>User Category: {userCategory}</div>
            </div>
          </div>
          <div className={classes.changePasswordLinkDiv}>
            <span className={classes.changePasswordLinkSpan} onClick={() => this.showPasswordUi()}>
              Change Password for Rep or SuperAdmin
            </span>
          </div>
          <div className={classForPasswordForm}>
            <div style={{ marginLeft: '10px', marginTop: '8px' }}>
              Choose User&nbsp;
              <select onChange={(event) => this.setState({ emailOfChosenUser: event.target.value })}>
                {this.state.specialUsers.map((u) => (
                  <option key={u.email} value={u.email}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <CrmTextField
                className={classes.textField}
                label="Old Password"
                password
                value={this.state.oldPassword}
                onChange={(event) => this.handlePasswordChange(event, 'oldPassword')}
              />
              <CrmTextField
                className={classes.textField}
                label="New Password"
                password
                value={this.state.newPassword}
                onChange={(event) => this.handlePasswordChange(event, 'newPassword')}
              />
              <CrmTextField
                className={classes.textField}
                label="Confirm New Password"
                password
                value={this.state.confirmPassword}
                onChange={(event) => this.handlePasswordChange(event, 'confirmPassword')}
              />
              <div className={classes.passwordButtonsDiv}>
                <CrmButton outlined onClick={() => this.hidePasswordUi()}>
                  Cancel
                </CrmButton>
                <CrmButton filled onClick={() => this.validateFormAndSubmitPasswordChange()}>
                  Submit
                </CrmButton>
              </div>
              <div />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withStyles(userProfilePanelStyles)(UserProfilePanel);
